import React, { memo } from 'react';
import { Star, Trophy, Gift, ExternalLink, Zap, Crown } from 'lucide-react';

const Offers = memo(() => {
  const offers = [
    {
      id: 1,
      name: 'PUB',
      url: 'https://www.pubcasino.co.uk/',
      bonus: '100% up to £100',
      description: 'A beloved UK casino brand that brings the authentic pub atmosphere to online gaming. With a focus on community, fairness, and genuine entertainment, PUB Casino delivers classic casino experiences with modern convenience and generous welcome offers.',
      features: ['Authentic UK Gaming', 'Community Focus', 'Fair Play Guarantee', 'Classic Casino Games'],
      color: 'from-blue-600 to-blue-800',
      icon: <Trophy className="h-8 w-8" />,
      layout: 'horizontal'
    },
    {
      id: 2,
      name: 'NRG',
      url: 'https://nrg.bet/',
      bonus: 'Get 200% on your first deposit + 100 free spins',
      description: 'An energetic gaming platform that combines high-energy entertainment with substantial rewards. NRG delivers explosive gaming experiences with generous bonuses, free spins, and a vibrant atmosphere that keeps players engaged and excited.',
      features: ['High-Energy Gaming', 'Generous Bonuses', 'Free Spins', 'Vibrant Experience'],
      color: 'from-green-600 to-green-800',
      icon: <Star className="h-8 w-8" />,
      layout: 'vertical'
    },
    {
      id: 3,
      name: 'Mr. Play',
      url: 'https://www.mrplay.com/',
      bonus: '100 spins + 100% up to £200 bonus',
      description: 'A sophisticated gaming destination that combines elegance with excitement. Mr. Play offers premium casino entertainment with substantial welcome packages, featuring both free spins and generous match bonuses for new players.',
      features: ['Sophisticated Gaming', 'Premium Experience', 'Free Spins', 'Generous Bonuses'],
      color: 'from-purple-600 to-purple-800',
      icon: <Crown className="h-8 w-8" />,
      layout: 'card'
    }
  ];

  return (
    <section id="offers" className="book-section bg-book-beige">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
            Exclusive Premium Offers
          </h2>
          <p className="text-xl text-book-brown-dark max-w-3xl mx-auto">
            Handpicked opportunities from Europe's most prestigious gaming platforms.
            Each offer crafted for the discerning player seeking exceptional value and thrilling experiences.
          </p>
        </div>

        {/* Modern 3-Column Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* PUB Casino Card */}
          <div className="group relative bg-white rounded-2xl shadow-book hover:shadow-2xl transition-all duration-300 overflow-hidden border border-book-brown-light">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-blue-800/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl mb-4">
                  <Trophy className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-book-brown mb-2">PUB Casino</h3>
                <div className="text-lg font-semibold text-blue-600 mb-1">100% up to £100</div>
                <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">Authentic UK Gaming</span>
              </div>

              {/* Description */}
              <p className="text-book-brown-dark text-center mb-6 leading-relaxed">
                A beloved UK casino brand that brings the authentic pub atmosphere to online gaming with community focus and fair play guarantee.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Zap className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">UK Gaming</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Crown className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Community</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Star className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Fair Play</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Gift className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Classic Games</span>
                </div>
              </div>

              {/* CTA Button */}
              <a
                href="https://www.pubcasino.co.uk/"
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                className="block w-full text-center bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Play at PUB Casino
              </a>
            </div>
          </div>

          {/* NRG Casino Card */}
          <div className="group relative bg-white rounded-2xl shadow-book hover:shadow-2xl transition-all duration-300 overflow-hidden border border-book-brown-light">
            <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 to-green-800/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-600 to-green-800 rounded-2xl mb-4">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-book-brown mb-2">NRG</h3>
                <div className="text-lg font-semibold text-green-600 mb-1">200% + 100 Free Spins</div>
                <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">High-Energy Gaming</span>
              </div>

              {/* Description */}
              <p className="text-book-brown-dark text-center mb-6 leading-relaxed">
                An energetic gaming platform that combines high-energy entertainment with substantial rewards and explosive gaming experiences.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Zap className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">High-Energy</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Crown className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Generous</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Star className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Free Spins</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Gift className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Vibrant</span>
                </div>
              </div>

              {/* CTA Button */}
              <a
                href="https://nrg.bet/"
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                className="block w-full text-center bg-gradient-to-r from-green-600 to-green-800 text-white py-3 rounded-xl hover:from-green-700 hover:to-green-900 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Play at NRG
              </a>
            </div>
          </div>

          {/* Mr. Play Casino Card */}
          <div className="group relative bg-white rounded-2xl shadow-book hover:shadow-2xl transition-all duration-300 overflow-hidden border border-book-brown-light">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-purple-800/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl mb-4">
                  <Crown className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-book-brown mb-2">Mr. Play</h3>
                <div className="text-lg font-semibold text-purple-600 mb-1">100 Spins + 100% up to £200</div>
                <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">Sophisticated Gaming</span>
              </div>

              {/* Description */}
              <p className="text-book-brown-dark text-center mb-6 leading-relaxed">
                A sophisticated gaming destination that combines elegance with excitement, offering premium casino entertainment with substantial welcome packages.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Zap className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Sophisticated</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Crown className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Premium</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Star className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Free Spins</span>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Gift className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-xs text-book-brown-dark font-medium">Generous</span>
                </div>
              </div>

              {/* CTA Button */}
              <a
                href="https://www.mrplay.com/"
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                className="block w-full text-center bg-gradient-to-r from-purple-600 to-purple-800 text-white py-3 rounded-xl hover:from-purple-700 hover:to-purple-900 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Play at Mr. Play
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

Offers.displayName = 'Offers';

export default Offers;
